import { createSignal, onCleanup, onMount, For } from 'solid-js';
import { useLocation } from '@solidjs/router';
import { Logo } from './Logo';
import { contacts, navItems } from '~/data/config';
import './Header.css';

interface Props {
  onRequestClick?: () => void;
}

function smoothScrollTo(hash: string) {
  if (!hash.startsWith('#')) return;
  const target = document.querySelector(hash);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function Header(props: Props) {
  const [scrolled, setScrolled] = createSignal(false);
  const [menuOpen, setMenuOpen] = createSignal(false);
  const primaryPhone = contacts.phones[0];
  const location = useLocation();
  const navHref = (href: string) =>
    location.pathname === '/' ? href : `/${href}`;

  onMount(() => {
    // Скролл-листенер на iOS дёргает state каждый кадр.
    // Батчим через rAF: одно обновление signal'а на репэйнт-frame,
    // никаких лишних reconciler-проходов SolidJS.
    let raf = 0;
    let pending = false;
    const apply = () => {
      pending = false;
      setScrolled(window.scrollY > 12);
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    onCleanup(() => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    });
  });

  const handleNav = (e: MouseEvent, href: string) => {
    if (!href.startsWith('#')) return;
    if (location.pathname !== '/') return; // let browser navigate home with hash
    e.preventDefault();
    smoothScrollTo(href);
    setMenuOpen(false);
  };

  const openRequest = () => {
    setMenuOpen(false);
    props.onRequestClick?.();
  };

  return (
    <header class="header" classList={{ 'header--scrolled': scrolled() }}>
      <div class="container header__row">
        <Logo />

        <nav class="header__nav" aria-label="Основная навигация">
          <ul class="header__nav-list">
            <For each={navItems}>
              {(item) => (
                <li>
                  <a
                    href={navHref(item.href)}
                    onClick={(e) => handleNav(e, item.href)}
                  >
                    {item.label}
                  </a>
                </li>
              )}
            </For>
          </ul>
        </nav>

        <div class="header__actions">
          <a class="header__phone" href={`tel:${primaryPhone.tel}`}>
            <span class="header__phone-label">Звонок по России</span>
            <span class="header__phone-value">{primaryPhone.value}</span>
          </a>
          <button class="btn btn--red header__cta" onClick={openRequest}>
            Оставить заявку
          </button>
          <button
            class="header__burger"
            aria-label={menuOpen() ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={menuOpen()}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span classList={{ 'is-open': menuOpen() }} />
            <span classList={{ 'is-open': menuOpen() }} />
            <span classList={{ 'is-open': menuOpen() }} />
          </button>
        </div>
      </div>

      <div class="mobile-menu" classList={{ 'mobile-menu--open': menuOpen() }} aria-hidden={!menuOpen()}>
        <ul>
          <For each={navItems}>
            {(item) => (
              <li>
                <a
                  href={navHref(item.href)}
                  onClick={(e) => handleNav(e, item.href)}
                >
                  {item.label}
                </a>
              </li>
            )}
          </For>
        </ul>
        <div class="mobile-menu__foot">
          <a class="mobile-menu__phone" href={`tel:${primaryPhone.tel}`}>
            {primaryPhone.value}
          </a>
          <button class="btn btn--red" onClick={openRequest}>
            Оставить заявку
          </button>
        </div>
      </div>
    </header>
  );
}
